import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AuthContext } from '@/features/auth/auth-context';
import { ApiError, errorKeyOf } from '@/services/api/errors';
import { OrganizationApi } from '@/services/api/organization-api';
import type { AddressDto, AddressRequest } from '@/services/api/types';

export interface UseOrganizationAddressesResult {
  data: AddressDto[];
  loading: boolean;
  loadError: string | null;
  saving: boolean;
  saveError: string | null;
  add: (request: AddressRequest) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  update: (id: string, request: AddressRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

export function useOrganizationAddresses(organization?: OrganizationApi): UseOrganizationAddressesResult {
  const auth = useContext(AuthContext);

  const injectedApi = useRef(organization);
  useEffect(() => {
    injectedApi.current = organization;
  }, [organization]);
  const realApiRef = useRef<OrganizationApi | null>(null);

  const getApi = useCallback((): OrganizationApi => {
    if (injectedApi.current) {
      return injectedApi.current;
    }
    const client = auth?.client;
    if (!client) {
      throw new ApiError('unknown', 'generic');
    }
    if (!realApiRef.current) {
      realApiRef.current = new OrganizationApi(client);
    }
    return realApiRef.current;
  }, [auth]);

  const [data, setData] = useState<AddressDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await getApi().getAddresses(controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setData(result);
        setLoadError(null);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'aborted') {
          return;
        }
        setLoadError(errorKeyOf(err));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [getApi]);

  const add = useCallback(
    async (request: AddressRequest): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);
      try {
        const created = await getApi().addAddress(request);
        setData((current) => [...current, created]);
        return true;
      } catch (err) {
        setSaveError(errorKeyOf(err));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getApi],
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);
      try {
        await getApi().deleteAddress(id);
        setData((current) => current.filter((address) => address.id !== id));
        return true;
      } catch (err) {
        setSaveError(errorKeyOf(err));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getApi],
  );

  const update = useCallback(
    async (id: string, request: AddressRequest): Promise<boolean> => {
      const removed = await remove(id);
      if (!removed) {
        return false;
      }
      return add(request);
    },
    [remove, add],
  );

  const clearSaveError = useCallback(() => setSaveError(null), []);

  return { data, loading, loadError, saving, saveError, add, remove, update, clearSaveError };
}
